import {SxProps, Theme, Tooltip, TooltipProps, Typography, useTheme} from "@mui/material";
import React, {forwardRef, RefAttributes} from "react";
import Box from "@mui/material/Box";
import {color_grey} from "@/agentur/AgenturColors";
import {blue} from "@mui/material/colors";
import {getContrastColor} from "@/lib/color-utils";

export type BadgeProps = {
    label?: string;
    icon?: React.ReactNode;
    color?: string;
    backgroundColor?: string;
    onClick?: (event: React.MouseEvent<HTMLElement>) => void;
    onDblClick?: (event: React.MouseEvent<HTMLElement>) => void;
    fullWidth?: boolean;
    tooltip?: string | React.ReactNode;
    tooltipPlacement?: TooltipProps["placement"];
};

export const Badge = forwardRef(
    (
        {
            label,
            icon,
            fullWidth,
            color,
            backgroundColor,
            onClick,
            onDblClick,
            tooltip,
            tooltipPlacement = "left-start"
        }: BadgeProps,
        ref: RefAttributes<any>["ref"]
    ) => {
        const theme = useTheme();
        if (label == null || label.length === 0) {
            return null;
        }

        const onKeyDown = () => {};

        const classes: SxProps<Theme> = {
            display: "inline-flex",
            gap: "4px",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "4px",
            verticalAlign: "middle",
            whiteSpace: "nowrap",
            letterSpacing: "0.05em\n",
            padding: "0px 8px 0px 8px",
            // boxShadow: "inset 0 0 0 1px rgba(40, 42, 44, 0.25)",
            backgroundColor: backgroundColor ?? blue[200],
            cursor: "default",
            width: fullWidth ? "100%" : "auto",
            height: "fit-content",
            "& .MuiTypography-root": {
                color: color || (backgroundColor && getContrastColor(backgroundColor)) || "black"
            },
            "&.active": {
                userSelect: "none",
                cursor: "pointer"
            },
            "&:hover.active": {
                borderColor: theme.palette.mode === "dark" ? color_grey[2] : "black",
                borderStyle: "solid",
                borderSize: 2,
                margin: -2
            },
            "& span": {
                textAlign: "center",
                overflow: "hidden",
                textOverflow: "ellipsis"
            }
        };

        const badge = (
            <Box
                ref={ref}
                className={(onClick || onDblClick) && "active"}
                sx={classes}
                role={(onClick || onDblClick) && "button"}
                onClick={onClick}
                onKeyDown={onKeyDown}
                onDoubleClick={onDblClick}
            >
                <span>{icon}</span>
                <Typography variant="caption" fontWeight="bold" textTransform="uppercase">
                    {label}
                </Typography>
            </Box>
        );

        return tooltip ? (
            <Tooltip arrow placement={tooltipPlacement} title={tooltip}>
                {badge}
            </Tooltip>
        ) : (
            badge
        );
    }
);

Badge.displayName = "Badge";
